import React, { useState, useEffect } from 'react';
import { StageProps } from '../types';
import DataService from '../services/dataService';
import { applyBestOf, collectRaceIds, effectivePoints } from '../utils/scoring';

const SeriesOverview: React.FC<StageProps> = ({ appData, setAppData, onNext }) => {
  const [seriesName, setSeriesName] = useState('');
  const [races, setRaces] = useState<Array<{ id: string; name: string; date: string; participantCount: number }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [editingRaceNames, setEditingRaceNames] = useState<Record<string, string>>({});
  const [editingRaceDates, setEditingRaceDates] = useState<Record<string, string>>({});

  useEffect(() => {
    // Initialize data from current series
    if (appData.currentSeriesMetadata) {
      setSeriesName(appData.currentSeriesMetadata.name);
    }

    if (appData.existingSeriesData && appData.existingSeriesData.length > 0) {
      // Extract race information from existing series data
      const raceIds = new Set<string>();
      const categorySet = new Set<string>();
      
      appData.existingSeriesData.forEach(participant => {
        if (participant.category) {
          categorySet.add(participant.category);
        }
        Object.keys(participant.races).forEach(raceId => {
          raceIds.add(raceId);
        });
      });

      // Build race list with stats
      const raceList = Array.from(raceIds).map(raceId => {
        const participantsInRace = appData.existingSeriesData!.filter(p => p.races[raceId]);
        const raceDate = participantsInRace.length > 0 ? participantsInRace[0].races[raceId].date : '';
        
        return {
          id: raceId,
          name: raceId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
          date: raceDate,
          participantCount: participantsInRace.length
        };
      });

      const sortedRaces = raceList.sort((a, b) => a.name.localeCompare(b.name));
      setRaces(sortedRaces);
      setCategories(Array.from(categorySet).sort());
      setTotalParticipants(appData.existingSeriesData.length);
      
      // Initialize editing state with current race names and dates
      const initialEditingNames: Record<string, string> = {};
      const initialEditingDates: Record<string, string> = {};
      sortedRaces.forEach(race => {
        initialEditingNames[race.id] = race.name;
        initialEditingDates[race.id] = race.date || '';
      });
      setEditingRaceNames(initialEditingNames);
      setEditingRaceDates(initialEditingDates);
    }
  }, [appData.currentSeriesMetadata, appData.existingSeriesData]);

  const handleSeriesNameSave = async () => {
    if (!appData.currentSeriesMetadata || !seriesName.trim()) return;
    
    setIsLoading(true);
    try {
      const dataService = DataService.getInstance();
      const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
      
      const updatedSeries = {
        ...currentSeries,
        metadata: {
          ...currentSeries.metadata,
          name: seriesName.trim(),
          lastModified: new Date().toISOString()
        }
      };
      
      await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);
      
      // Update local app data
      setAppData({
        ...appData,
        currentSeriesMetadata: {
          ...appData.currentSeriesMetadata,
          name: seriesName.trim()
        }
      });
      
      console.log('Series name updated successfully');
    } catch (error) {
      console.error('Failed to update series name:', error);
      alert('Failed to update series name. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRaceDelete = async (raceId: string) => {
    if (!appData.currentSeriesMetadata || !window.confirm(`Are you sure you want to delete the race "${races.find(r => r.id === raceId)?.name}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      const dataService = DataService.getInstance();
      const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
      
      // Remove race from all participants and recompute totals / best-of
      const bestOfCount = currentSeries.metadata?.bestOfRaces || currentSeries.bestOfRaces;
      const updatedParticipants = applyBestOf(
        currentSeries.participants.map(participant => {
          const updatedRaces = { ...participant.races };
          delete updatedRaces[raceId];
          return { ...participant, races: updatedRaces };
        }),
        bestOfCount
      );

      const remainingRaces = collectRaceIds(updatedParticipants);
      const fieldConfigs = (currentSeries.publishingSettings?.fieldConfigs || []).filter(
        (c) => c.type !== 'race' || remainingRaces.includes(c.fieldName)
      );
      
      const updatedSeries = {
        ...currentSeries,
        participants: updatedParticipants,
        publishingSettings: {
          ...currentSeries.publishingSettings,
          fieldConfigs,
          isPublished: currentSeries.publishingSettings?.isPublished || false,
        },
        metadata: {
          ...currentSeries.metadata,
          lastModified: new Date().toISOString()
        }
      };
      
      await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);

      if (updatedSeries.publishingSettings?.isPublished) {
        const selected = fieldConfigs.filter((c) => c.isSelected).sort((a, b) => a.order - b.order);
        const selectedFields = selected.filter((c) => c.type === 'participant').map((c) => c.fieldName);
        const selectedRaces = selected.filter((c) => c.type === 'race').map((c) => c.fieldName);
        const bestOfColumnSelected = selectedFields.includes('bestOfPoints');
        const sorted = [...updatedParticipants].sort((a, b) => effectivePoints(b) - effectivePoints(a));
        await dataService.publishResults({
          seriesName: updatedSeries.metadata.name,
          generatedAt: new Date().toISOString(),
          selectedFields,
          selectedRaces,
          fieldCaptions: Object.fromEntries(selected.map((c) => [c.fieldName, c.caption])),
          fieldOrder: selected.map((c) => ({ fieldName: c.fieldName, type: c.type, order: c.order })),
          participants: sorted.map((participant, index) => ({
            position: index + 1,
            category: participant.category,
            ...Object.fromEntries(selectedFields.map((field) => [
              field,
              field === 'totalPoints' && bestOfCount && !bestOfColumnSelected
                ? effectivePoints(participant)
                : (participant as any)[field],
            ])),
            bestOfPoints: participant.bestOfPoints,
            effectivePoints: effectivePoints(participant),
            races: Object.fromEntries(
              selectedRaces
                .map((id) => [id, participant.races[id]] as const)
                .filter(([, v]) => v)
            ),
          })),
        });
      }
      
      // Update local app data
      setAppData({
        ...appData,
        existingSeriesData: updatedParticipants,
        finalResults: updatedParticipants,
        currentSeriesMetadata: {
          ...appData.currentSeriesMetadata,
          publishingSettings: updatedSeries.publishingSettings,
          lastModified: updatedSeries.metadata.lastModified,
        }
      });
      
      // Update local race list
      setRaces(races.filter(race => race.id !== raceId));
      
      console.log('Race deleted successfully');
    } catch (error) {
      console.error('Failed to delete race:', error);
      alert('Failed to delete race. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle inline race name editing
  const updateRaceNameInput = (raceId: string, newName: string) => {
    setEditingRaceNames({
      ...editingRaceNames,
      [raceId]: newName
    });
  };

  const saveRaceName = async (raceId: string) => {
    const newName = editingRaceNames[raceId];
    if (!newName || newName.trim() === '') return;
    
    const currentRace = races.find(r => r.id === raceId);
    if (!currentRace || newName.trim() === currentRace.name) {
      // No change needed, clear editing state
      const updatedEditing = { ...editingRaceNames };
      delete updatedEditing[raceId];
      setEditingRaceNames(updatedEditing);
      return;
    }
    
    await handleRaceRename(raceId, newName.trim());
    
    // Clear editing state after successful rename
    const updatedEditing = { ...editingRaceNames };
    delete updatedEditing[raceId];
    setEditingRaceNames(updatedEditing);
  };

  // Handle inline race date editing
  const updateRaceDateInput = (raceId: string, newDate: string) => {
    setEditingRaceDates({
      ...editingRaceDates,
      [raceId]: newDate
    });
  };

  const saveRaceDate = async (raceId: string) => {
    const newDate = editingRaceDates[raceId];
    const currentRace = races.find(r => r.id === raceId);
    
    if (!currentRace || newDate === currentRace.date) {
      // No change needed, clear editing state
      const updatedEditing = { ...editingRaceDates };
      delete updatedEditing[raceId];
      setEditingRaceDates(updatedEditing);
      return;
    }
    
    await handleRaceDateUpdate(raceId, newDate);
    
    // Clear editing state after successful update
    const updatedEditing = { ...editingRaceDates };
    delete updatedEditing[raceId];
    setEditingRaceDates(updatedEditing);
  };

  const handleRaceDateUpdate = async (raceId: string, newDate: string) => {
    if (!appData.currentSeriesMetadata) return;
    
    setIsLoading(true);
    try {
      const dataService = DataService.getInstance();
      const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
      
      // Update race date in all participants
      const updatedParticipants = currentSeries.participants.map(participant => {
        const updatedRaces = { ...participant.races };
        if (updatedRaces[raceId]) {
          updatedRaces[raceId] = {
            ...updatedRaces[raceId],
            date: newDate
          };
        }
        
        return {
          ...participant,
          races: updatedRaces
        };
      });
      
      const updatedSeries = {
        ...currentSeries,
        participants: updatedParticipants,
        metadata: {
          ...currentSeries.metadata,
          lastModified: new Date().toISOString()
        }
      };
      
      await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);
      
      // Update local app data
      setAppData({
        ...appData,
        existingSeriesData: updatedParticipants
      });
      
      // Update local race list
      setRaces(races.map(race => 
        race.id === raceId ? { ...race, date: newDate } : race
      ));
      
      console.log('Race date updated successfully');
    } catch (error) {
      console.error('Failed to update race date:', error);
      alert('Failed to update race date. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRaceRename = async (raceId: string, newName: string) => {
    if (!appData.currentSeriesMetadata || !newName.trim()) return;
    
    const newRaceId = newName.toLowerCase().replace(/\s+/g, '_');
    if (newRaceId === raceId) return; // No change needed
    
    setIsLoading(true);
    try {
      const dataService = DataService.getInstance();
      const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
      
      // Rename race in all participants
      const updatedParticipants = currentSeries.participants.map(participant => {
        const updatedRaces = { ...participant.races };
        if (updatedRaces[raceId]) {
          updatedRaces[newRaceId] = updatedRaces[raceId];
          delete updatedRaces[raceId];
        }
        
        return {
          ...participant,
          races: updatedRaces
        };
      });
      
      const updatedSeries = {
        ...currentSeries,
        participants: updatedParticipants,
        metadata: {
          ...currentSeries.metadata,
          lastModified: new Date().toISOString()
        }
      };
      
      await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);
      
      // Update local app data
      setAppData({
        ...appData,
        existingSeriesData: updatedParticipants
      });
      
      // Update local race list
      setRaces(races.map(race => 
        race.id === raceId ? { ...race, id: newRaceId, name: newName } : race
      ));
      
      console.log('Race renamed successfully');
    } catch (error) {
      console.error('Failed to rename race:', error);
      alert('Failed to rename race. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRace = () => {
    // Navigate to upload stage to add a new race
    onNext?.();
  };

  const handleViewResults = () => {
    // Navigate to results stage with properly formatted data
    if (appData.existingSeriesData && appData.existingSeriesData.length > 0) {
      console.log('SeriesOverview: Preparing results data for navigation');
      console.log('Existing series data:', appData.existingSeriesData);
      
      // Ensure the data is properly sorted by total points (or best-of points if configured)
      const sortedResults = [...appData.existingSeriesData].sort((a, b) => {
        const aPoints = a.bestOfPoints || a.totalPoints;
        const bPoints = b.bestOfPoints || b.totalPoints;
        return bPoints - aPoints;
      });
      
      console.log('Sorted results for navigation:', sortedResults.map(r => ({ 
        name: r.name, 
        totalPoints: r.totalPoints, 
        bestOfPoints: r.bestOfPoints,
        races: Object.keys(r.races || {})
      })));
      
      setAppData({
        ...appData,
        finalResults: sortedResults
      });
      
      // We need a custom navigation function here - will handle in App.tsx
      (window as any).__navigateToResults?.();
    }
  };

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>📊 Series Overview</h2>
        <p>Manage your race series settings, races, and participants</p>
      </div>

      {/* Series Name Section */}
      <div className="form-group">
        <label htmlFor="seriesName">Series Name</label>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input
            id="seriesName"
            type="text"
            className="form-control"
            value={seriesName}
            onChange={(e) => setSeriesName(e.target.value)}
            placeholder="Enter series name..."
            style={{ flex: 1, fontSize: '1.1rem', fontWeight: '600' }}
          />
          <button 
            className="btn btn-secondary"
            onClick={handleSeriesNameSave}
            disabled={isLoading || !seriesName.trim() || seriesName === appData.currentSeriesMetadata?.name}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Races Section */}
      <div className="form-group">
        <label>Races in Series ({races.length})</label>
        
        {races.length > 0 ? (
          <div style={{ 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px', 
            overflow: 'hidden',
            backgroundColor: 'white'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '180px 140px 100px 60px',
              gap: '0',
              backgroundColor: '#f7fafc',
              borderBottom: '2px solid #e2e8f0',
              padding: '0.75rem',
              fontWeight: '600',
              fontSize: '0.9rem',
              color: '#4a5568'
            }}>
              <div>Race Name</div>
              <div>Date</div>
              <div>Participants</div>
              <div>Delete</div>
            </div>
            
            {races.map((race, index) => (
              <div
                key={race.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 140px 100px 60px',
                  gap: '0',
                  padding: '0.75rem',
                  borderBottom: index < races.length - 1 ? '1px solid #e2e8f0' : 'none',
                  backgroundColor: 'white',
                  fontSize: '0.9rem'
                }}
              >
                {/* Race Name - Wider Input */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🏁 
                  <input
                    type="text"
                    value={editingRaceNames[race.id] || race.name}
                    onChange={(e) => updateRaceNameInput(race.id, e.target.value)}
                    onBlur={() => saveRaceName(race.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      backgroundColor: '#f7fafc',
                      width: '120px'
                    }}
                    disabled={isLoading}
                    placeholder="Race 1"
                  />
                </div>
                
                {/* Date - Editable Input */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center'
                }}>
                  <input
                    type="date"
                    value={editingRaceDates[race.id] || race.date || ''}
                    onChange={(e) => updateRaceDateInput(race.id, e.target.value)}
                    onBlur={() => saveRaceDate(race.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      padding: '0.3rem 0.5rem',
                      fontSize: '0.9rem',
                      backgroundColor: 'white',
                      width: '120px',
                      color: '#2d3748',
                      fontFamily: 'inherit'
                    }}
                    disabled={isLoading}
                    placeholder="Select date"
                  />
                </div>
                
                {/* Participants Count */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: '#718096',
                  fontSize: '0.8rem'
                }}>
                  {race.participantCount} racers
                </div>
                
                {/* Delete Button */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <button
                    className="btn"
                    onClick={() => handleRaceDelete(race.id)}
                    disabled={isLoading}
                    style={{ 
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      backgroundColor: '#fed7d7',
                      border: '1px solid #fc8181',
                      color: '#c53030'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: '#f7fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            color: '#718096'
          }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>🏁 No races in this series yet</p>
            <p>Add your first race to get started!</p>
          </div>
        )}
      </div>

      {/* Series Statistics */}
      <div className="form-group">
        <label>Series Statistics</label>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: '#e6fffa',
            border: '1px solid #38b2ac',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#285e61' }}>
              {totalParticipants}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#2c7a7b', fontWeight: '600' }}>
              Total Participants
            </div>
          </div>
          
          <div style={{
            padding: '1rem',
            backgroundColor: '#fef5e7',
            border: '1px solid #d69e2e',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#b7791f' }}>
              {categories.length}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#c05621', fontWeight: '600' }}>
              Categories
            </div>
          </div>
          
          <div style={{
            padding: '1rem',
            backgroundColor: '#ebf8ff',
            border: '1px solid #3182ce',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#2a69ac' }}>
              {races.length}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#2c5282', fontWeight: '600' }}>
              Races Complete
            </div>
          </div>
          
          {appData.currentSeriesMetadata?.bestOfRaces && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f0fff4',
              border: '1px solid #38a169',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#276749' }}>
                {appData.currentSeriesMetadata.bestOfRaces}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#2f855a', fontWeight: '600' }}>
                Best of {appData.currentSeriesMetadata.bestOfRaces} Scoring
              </div>
            </div>
          )}
        </div>
        
        {categories.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <small style={{ color: '#718096', fontWeight: '600' }}>Categories: </small>
            <small style={{ color: '#4a5568' }}>
              {categories.join(', ')}
            </small>
          </div>
        )}
      </div>

      {/* Navigation Actions */}
      <div className="stage-actions">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {totalParticipants > 0 && (
            <button 
              className="btn btn-secondary"
              onClick={handleViewResults}
              disabled={isLoading}
            >
              📊 View & Manage Results
            </button>
          )}
          
          <button 
            className="btn btn-primary"
            onClick={handleAddRace}
            disabled={isLoading}
          >
            🏁 Add Another Race
          </button>
        </div>
      </div>

      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>⏳ Processing...</div>
            <div style={{ color: '#718096' }}>Please wait while we update your series</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeriesOverview; 